import React, { useMemo } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { Flex, Link, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { NavbarDropdown } from "@jsq/ds/layouts";
import { useFeatureFlag } from "@jsq/flags-react";
import {
  getCCLogsPath,
  getFeatureFlagsPath,
  getGPArenasPath,
  getSessionsPath,
  getToolsPath,
  getUsersPath,
} from "~/common/helpers/url";
import { useAppContext } from "~/contexts/appContext";
import { globalEnv } from "~/global-env";
import { useComplianceEntityOnboardingEnabled } from "~/pages/Compliance/flags";
import {
  canAccessFayAI,
  canAccessManagerView,
  hasBillingFARole,
  hasBillingGPRole,
  hasOpcoImportPermission,
  hasTreasuryAccessOnAnyArena,
  hasTreasuryConfigAccess,
  isAdminUser,
  isBillingUser,
  isCanopyAnalyst,
  isComplianceUser,
  isControlTowerUser,
  isDataOnboardingFundAdminManager,
  isDataOnboardingUserOrManager,
  isDataStudioUser,
  isDataVaultAdmin,
  isDataVaultUser,
  isDexUser,
  isEnablementUser,
  isFundAccountingController,
  isGpWorkspaceAccessProvision,
  isTreasuryAdmin,
  isTreasuryUser,
} from "~/utils/userRoles";
import { HomeTab } from "./HomeTab";
import * as styles from "./styles_vanilla.css";
import { NavGroup } from "./types";

const SECTION_LABELS = {
  CUSTOMER_SUCCESS: "Customer Success",
  BILLING: "Billing",
  CCTOOLS: "CCTools",
  ENTITY_DATA: "Entity Data",
  DOCUMENTS: "Documents",
} as const;

const normalize = (path: string) => path.split("?")[0].replace(/\/+$/, "");

export type NavGroupDropdownProps = { group: NavGroup };

const NavGroupDropdown = ({ group }: NavGroupDropdownProps) => {
  const { pathname } = useLocation();
  const visibleItems = group.items.filter((item) => item.visible);

  if (visibleItems.length === 0) return null;

  if (group.standalone) {
    const item = visibleItems[0];
    return <HomeTab tabKey={item.key} tabLabel={item.label} route={item.route} />;
  }

  const isActive = visibleItems.some((item) => {
    const itemPath = normalize(item.route);
    const currentPath = normalize(pathname);
    return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
  });

  return (
    <NavbarDropdown
      position="alignWithLeftMarginButton"
      theme="dark"
      dropDownClassName={styles.dropdownStyle}
      buttonClassName={clsx(isActive && styles.activeNavGroupButton)}
      data-cy={`${group.key}-nav-group`}
      actionItem={
        <Flex
          alignItems="center"
          gap="4px"
          className={clsx(styles.navGroupLabel, isActive && styles.navGroupLabelActive)}
        >
          <Text className={styles.navGroupLabelText}>{group.label}</Text>
          <Icon type="chevron-down" size="small" />
        </Flex>
      }
    >
      {visibleItems.map((item, index) => {
        const prevItem = index > 0 ? visibleItems[index - 1] : null;
        const showHeader =
          item.sectionLabel !== undefined && item.sectionLabel !== prevItem?.sectionLabel;
        return (
          <React.Fragment key={item.key}>
            {showHeader && <Text className={styles.navSectionHeader}>{item.sectionLabel}</Text>}
            <Link
              to={item.route}
              variant="neutral"
              className={styles.navDropdownItem}
              data-cy={item.dataCy}
            >
              {item.label}
            </Link>
          </React.Fragment>
        );
      })}
    </NavbarDropdown>
  );
};

export const NavConfig = () => {
  const { user } = useAppContext();
  const canopyWorkspacesAccessFF = useFeatureFlag("canopyWorkspaceAccess", false);
  const reportingFF = useFeatureFlag("reportsPDFM1", false);
  const isControlTowerEnabled = useFeatureFlag("showCanopyControlTower", false);
  const isManagerViewEnabled = useFeatureFlag("showManagerView", false);
  const fayAIInCanopyFF = useFeatureFlag("showFayAIInCanopy", false);
  const showDexFF = useFeatureFlag("showDex", false);
  const showDocumentVaultFF = useFeatureFlag("showDocumentVault", false);
  const showCashProcessingPageFF = useFeatureFlag("showCashProcessingPage", false);
  const showTransactionLookupFF = useFeatureFlag("showTransactionLookup", false);
  const billingFAManualTaggingFF = useFeatureFlag("billingFAManualTagging", false);
  const showCanopyEumOverrideFF = useFeatureFlag("showCanopyEumOverride", false);
  const showComplianceProfileRefreshTabFF = useFeatureFlag(
    "showComplianceProfileRefreshTab",
    false,
  );
  // Shares useComplianceEntityOnboardingEnabled with the Compliance page's
  // Clients tab gate (src/pages/Compliance/index.tsx) so the nav entry and
  // the tab it links to can't drift onto different flags.
  const complianceEntityOnboardingEnabledFF = useComplianceEntityOnboardingEnabled();
  const treasuryClientLevelPermissionsFF = useFeatureFlag(
    "treasuryClientLevelPermissions",
    false,
  );

  const navGroups = useMemo<NavGroup[]>(() => {
    const roles = (user?.roles || []).filter((role): role is string => !!role);

    const isCompliance = isComplianceUser(roles);
    const isTreasury = treasuryClientLevelPermissionsFF
      ? hasTreasuryAccessOnAnyArena(roles)
      : isTreasuryUser(roles);
    const isDataStudio = isDataStudioUser(roles);
    const isDsFundAdminManager = isDataOnboardingFundAdminManager(roles);
    const isDsUserOrManager = isDataOnboardingUserOrManager(roles);
    const hasOpcoImport = hasOpcoImportPermission(roles);
    const isDataVault = isDataVaultUser(roles);
    const isDataVaultAdminUser = isDataVaultAdmin(roles);
    const isDex = isDexUser(roles);
    const isAdmin = isAdminUser(roles);
    const isBilling = isBillingUser(roles);
    const hasBillingFA = hasBillingFARole(roles);
    const hasBillingGP = hasBillingGPRole(roles);
    const isGpWorkspace = isGpWorkspaceAccessProvision(roles) && canopyWorkspacesAccessFF;
    const isControlTower = isControlTowerUser(roles) && isControlTowerEnabled;
    const showManagerTabs = canAccessManagerView(roles) && isManagerViewEnabled;
    const isEnablement = isEnablementUser(roles);
    const isAnalyst = isCanopyAnalyst(roles);
    const canAccessUsers = isAdmin && (user?.canAccessUsers ?? false);
    const canUseFeatureFlags = isAdmin && (user?.canUseFeatureFlags ?? false);
    const isFaController = isFundAccountingController(roles);

    return [
      {
        key: "control-tower",
        label: "Control Tower",
        items: [
          {
            key: "your-workspaces",
            label: "Your workspaces",
            route: globalEnv.yourWorkspacesRoute,
            visible: canopyWorkspacesAccessFF,
            dataCy: "your-workspaces-tab",
          },
          {
            key: "workflows",
            label: "Workflows",
            route: `${globalEnv.controlTowerRoute}/workflows`,
            visible: isControlTower,
            dataCy: "control-tower-tab",
          },
          {
            key: "clients",
            label: "Clients",
            route: `${globalEnv.controlTowerRoute}/clients`,
            visible: showManagerTabs,
            dataCy: "clients-tab",
          },
          {
            key: "individuals",
            label: "Individuals",
            route: `${globalEnv.controlTowerRoute}/individuals`,
            visible: showManagerTabs,
            dataCy: "individuals-tab",
          },
        ],
      },
      {
        key: "accounting",
        label: "Accounting",
        items: [
          {
            key: "transaction-sync",
            label: "Transaction Sync",
            route: globalEnv.fundAccountingRoute,
            visible: true,
            dataCy: "fund-accounting-tab",
          },
          {
            key: "cash-processing",
            label: "Cash Processing",
            route: `${globalEnv.fundAccountingRoute}/cash-processing`,
            visible: showCashProcessingPageFF,
            dataCy: "accounting-cash-processing-tab",
          },
          {
            key: "admin-oversight-agent",
            label: "Admin oversight agent",
            route: globalEnv.fayAIRoute,
            // Shares canAccessFayAI with the src/index.tsx route gate so the nav
            // entry point and the route it links to can't drift onto different
            // flags.
            visible: canAccessFayAI(roles, fayAIInCanopyFF),
            dataCy: "admin-oversight-agent-tab",
          },
          {
            key: "transaction-lookup",
            label: "Transaction Lookup",
            route: `${globalEnv.fundAccountingRoute}/transaction-lookup`,
            visible: isFaController && showTransactionLookupFF,
            dataCy: "accounting-transaction-lookup-tab",
          },
        ],
      },
      {
        key: "compliance",
        label: "Compliance",
        items: [
          {
            key: "cases",
            label: "Cases",
            route: globalEnv.complianceRoute,
            visible: isCompliance,
            dataCy: "compliance-tab",
          },
          {
            key: "escalations",
            label: "Escalations",
            route: `${globalEnv.complianceRoute}/escalations`,
            visible: isCompliance,
          },
          {
            key: "positions",
            label: "Positions",
            route: `${globalEnv.complianceRoute}/positions`,
            visible: isCompliance,
          },
          {
            key: "entities",
            label: "Entities",
            route: `${globalEnv.complianceRoute}/entities`,
            visible: isCompliance,
          },
          {
            key: "compliance-clients",
            label: "Clients",
            route: `${globalEnv.complianceRoute}/clients`,
            visible: isCompliance && complianceEntityOnboardingEnabledFF,
          },
          {
            key: "profiles",
            label: "Profiles",
            route: `${globalEnv.complianceRoute}/profiles`,
            visible: isCompliance && showComplianceProfileRefreshTabFF,
          },
        ],
      },
      {
        key: "treasury",
        label: "Treasury",
        items: [
          {
            key: "transactions",
            label: "Transactions",
            route: `${globalEnv.treasuryRoute}/transactions`,
            visible: isTreasury,
          },
          {
            key: "payments",
            label: "Payments",
            route: `${globalEnv.treasuryRoute}/payments`,
            visible: isTreasury,
            dataCy: "treasury-tab",
          },
          {
            key: "payees",
            label: "Payees",
            route: `${globalEnv.treasuryRoute}/payees`,
            visible: isTreasury,
          },
          {
            key: "configuration",
            label: "Configuration",
            route: `${globalEnv.treasuryRoute}/configuration`,
            visible: treasuryClientLevelPermissionsFF
              ? isTreasuryAdmin(roles) || hasTreasuryConfigAccess(roles)
              : hasTreasuryConfigAccess(roles),
          },
        ],
      },
      {
        key: "data-studio",
        label: "Data Studio",
        items: [
          {
            key: "dashboard",
            label: "Dashboard",
            route: `${globalEnv.dataStudioRoute}/dashboard`,
            visible: isDataStudio,
            dataCy: "data-studio-tab",
          },
          {
            key: "crm-import",
            label: "CRM Import",
            route: `${globalEnv.dataStudioRoute}/crm-import`,
            visible: isDsUserOrManager,
            dataCy: "data-studio-crm-import-tab",
          },
          {
            key: "opco-import",
            label: "OpCo Import",
            route: `${globalEnv.dataStudioRoute}/opco-import`,
            visible: isDsFundAdminManager || isDsUserOrManager || hasOpcoImport,
            dataCy: "data-studio-opco-import-tab",
          },
          {
            key: "arena-creation",
            label: "Arena Creation",
            route: `${globalEnv.dataStudioRoute}/arena-creation`,
            visible: isDsUserOrManager,
            dataCy: "data-studio-arena-creation-tab",
          },
          {
            key: "arena-lock",
            label: "Arena Lock/Unlock",
            route: `${globalEnv.dataStudioRoute}/arena-lock`,
            visible: isDsFundAdminManager || isDsUserOrManager,
            dataCy: "data-studio-arena-lock-tab",
          },
        ],
      },
      {
        key: "reporting",
        label: "Reporting",
        standalone: true,
        items: [
          {
            key: "reporting",
            label: "Reporting",
            route: globalEnv.reportingRoute,
            visible: reportingFF,
          },
        ],
      },
      {
        key: "dex",
        label: "Dex",
        standalone: true,
        items: [
          {
            key: "dex",
            label: "Dex",
            route: globalEnv.dexRoute,
            visible: showDexFF && isDex,
            dataCy: "dex-tab",
          },
        ],
      },
      {
        key: "client-tools",
        label: "Client Tools",
        items: [
          {
            key: "in-app-announcements",
            label: "In-app announcements",
            route: globalEnv.enablementRoute,
            visible: isEnablement,
            sectionLabel: SECTION_LABELS.CUSTOMER_SUCCESS,
            dataCy: "enablement-tab",
          },
          {
            key: "analytics",
            label: "Analytics",
            route: globalEnv.analyticsRoute,
            visible: isAnalyst,
            sectionLabel: SECTION_LABELS.CUSTOMER_SUCCESS,
            dataCy: "analytics-tab",
          },
          {
            key: "billing-clients",
            label: "Clients",
            route: globalEnv.billingRoute,
            visible: isBilling,
            sectionLabel: SECTION_LABELS.BILLING,
            dataCy: "billing-tab",
          },
          {
            key: "billing-entities",
            label: "Entities",
            route: `${globalEnv.billingRoute}?tab=entities`,
            visible: isBilling && billingFAManualTaggingFF,
            sectionLabel: SECTION_LABELS.BILLING,
          },
          {
            key: "billing-cases",
            label: "Cases",
            route: `${globalEnv.billingRoute}?tab=cases`,
            visible:
              (hasBillingFA && billingFAManualTaggingFF) ||
              (hasBillingGP && showCanopyEumOverrideFF),
            sectionLabel: SECTION_LABELS.BILLING,
          },
          {
            key: "gp-arenas",
            label: "GP Arenas",
            route: getGPArenasPath(),
            visible: isAdmin,
            sectionLabel: SECTION_LABELS.CCTOOLS,
            dataCy: "cctools-gp-arenas-tab",
          },
          {
            key: "session",
            label: "Session",
            route: getSessionsPath(),
            visible: isAdmin,
            sectionLabel: SECTION_LABELS.CCTOOLS,
            dataCy: "cctools-session-tab",
          },
          {
            key: "users",
            label: "Users",
            route: getUsersPath(),
            visible: canAccessUsers,
            sectionLabel: SECTION_LABELS.CCTOOLS,
            dataCy: "cctools-users-tab",
          },
          {
            key: "cclogs",
            label: "CCLogs",
            route: getCCLogsPath(),
            visible: isAdmin,
            sectionLabel: SECTION_LABELS.CCTOOLS,
            dataCy: "cctools-cclogs-tab",
          },
          {
            key: "data-tools",
            label: "Data Tools",
            route: getToolsPath(),
            visible: isAdmin,
            sectionLabel: SECTION_LABELS.CCTOOLS,
            dataCy: "DataToolsTab",
          },
          {
            key: "feature-flags",
            label: "Feature Flags",
            route: getFeatureFlagsPath(),
            visible: canUseFeatureFlags,
            sectionLabel: SECTION_LABELS.CCTOOLS,
            dataCy: "cctools-feature-flags-tab",
          },
        ],
      },
      {
        key: "core-data",
        label: "Core Data",
        items: [
          {
            key: "custom-fields",
            label: "Custom Fields",
            route: `${globalEnv.dataVaultRoute}/custom-fields`,
            visible: isDataVaultAdminUser,
            sectionLabel: SECTION_LABELS.ENTITY_DATA,
            dataCy: "core-data-custom-fields-tab",
          },
          {
            key: "entities",
            label: "Entities",
            route: `${globalEnv.dataVaultRoute}/entities`,
            visible: isDataVault,
            sectionLabel: SECTION_LABELS.ENTITY_DATA,
            dataCy: "data-vault-tab",
          },
          {
            key: "bulk-upload",
            label: "Bulk Upload",
            route: `${globalEnv.dataVaultRoute}/entities/bulk-upload`,
            visible: isDataVault,
            sectionLabel: SECTION_LABELS.ENTITY_DATA,
            dataCy: "core-data-bulk-upload-tab",
          },
          {
            key: "documents",
            label: "Documents",
            route: globalEnv.documentVaultRoute,
            visible: showDocumentVaultFF && isDataVaultAdminUser,
            sectionLabel: SECTION_LABELS.DOCUMENTS,
            dataCy: "core-data-documents-tab",
          },
        ],
      },
      {
        key: "user-management",
        label: "User Management",
        items: [
          {
            key: "users",
            label: "Users",
            route: globalEnv.userAndRolesRoute,
            visible: isGpWorkspace,
            dataCy: "users-and-roles-tab",
          },
          {
            key: "workspaces",
            label: "Workspaces",
            route: `${globalEnv.userAndRolesRoute}/workspaces`,
            visible: isGpWorkspace,
            dataCy: "user-management-workspaces-tab",
          },
          {
            key: "canopy-roles",
            label: "Canopy Roles",
            route: `${globalEnv.userAndRolesRoute}/canopy-roles`,
            visible: isGpWorkspace,
            dataCy: "user-management-canopy-roles-tab",
          },
          {
            key: "workspace-roles",
            label: "Workspace Roles",
            route: `${globalEnv.userAndRolesRoute}/workspace-roles`,
            visible: isGpWorkspace,
            dataCy: "user-management-workspace-roles-tab",
          },
        ],
      },
    ];
  }, [
    user?.roles,
    user?.canAccessUsers,
    user?.canUseFeatureFlags,
    canopyWorkspacesAccessFF,
    showCanopyEumOverrideFF,
    reportingFF,
    isControlTowerEnabled,
    isManagerViewEnabled,
    fayAIInCanopyFF,
    showDexFF,
    showDocumentVaultFF,
    showCashProcessingPageFF,
    showTransactionLookupFF,
    billingFAManualTaggingFF,
    showComplianceProfileRefreshTabFF,
    complianceEntityOnboardingEnabledFF,
    treasuryClientLevelPermissionsFF,
  ]);

  return (
    <>
      {navGroups.map((group) => (
        <NavGroupDropdown key={group.key} group={group} />
      ))}
    </>
  );
};
